import { useState } from 'react';
import { Icon } from './Icon';
import { Avatar } from './Avatar';
import { useClickOutside } from '../../lib/useClickOutside';

// The one filter control used by every filter in the app (Status, Role, Staff,
// Show, and — via ChartDropdown — the daily chart Group/Type selectors). A pill,
// [type-icon] "Label:" [value], that opens a popover of options (radios for single,
// checkboxes for multi). The value reads as a green chip; each option carries its
// own marker — the app's real icon, or a person's avatar (see optionMark). Single
// mode can be `clearable={false}` for a required selector that never empties to
// "All". The popover reuses the shared `.popover` surface primitive.

export interface FilterOption {
  key: string;
  label: string;
  // The option's global icon (Icon name) — the real glyph the app uses for this
  // thing elsewhere (a status icon, a role icon), not a bespoke marker.
  icon?: string;
  // A person/dog initials avatar as the marker instead of an icon — for options
  // that ARE people (the Rota staff filter), where the face reads faster than a glyph.
  avatar?: { name: string; id?: string };
  // Optional tint for the icon (or a fallback colour dot) — a token expression,
  // e.g. 'var(--st-confirmed)'.
  dot?: string;
}

// The option's leading marker — an avatar if it's a person, else its real icon
// (tinted by tone), else a plain colour dot. Rendered in BOTH the open dropdown
// row and the closed pill's selection chip, so the marker never vanishes on close.
function optionMark(o: FilterOption, size: number) {
  if (o.avatar) return <Avatar name={o.avatar.name} id={o.avatar.id} size={size} />;
  if (o.icon)
    return (
      <span className="fc-opt-ico" style={o.dot ? { color: o.dot } : undefined}>
        <Icon name={o.icon} size={size} />
      </span>
    );
  if (o.dot) return <span className="fc-dot" style={{ background: o.dot }} />;
  return null;
}

interface FilterControlProps {
  label: string; // the filter TYPE, e.g. "Status" — always shown in the pill
  icon?: string; // the filter-type glyph (Icon name), shown left of the label
  options: FilterOption[];
  selected: string[]; // selected keys (empty = inactive)
  onChange: (keys: string[]) => void;
  mode?: 'single' | 'multi'; // default multi
  // A required single-select (e.g. a chart selector) can never be emptied to "All":
  // clicking the active option is a no-op and there's no Clear. Default true.
  clearable?: boolean;
  // Inapplicable HERE, rather than absent. A filter that unmounts when it stops
  // applying reflows every control beside it, under the cursor that just clicked
  // — so it stays, greyed and inert, and `disabledHint` says why on hover.
  disabled?: boolean;
  disabledHint?: string;
  // A single-select whose "everything" is a ROW in the menu rather than the
  // absence of a choice (the Bookings Type filter's All). When the selection is
  // this key the pill reads exactly as the unset state — "All", uncoloured — so
  // the pill family stays one language, while the menu marks the row chosen.
  allKey?: string;
  // CONDENSED, for a control that sits inside a row rather than on a toolbar —
  // the booking modal's per-dog area picker, where the pill shares a grid cell
  // with the dog's name and breed (Freddi, 2026-09-11: "we need a smaller
  // dropdown picker ... so it fits the smaller spaces better").
  //
  // It is the SAME control, tightened — not a second one. The pill drops the
  // "Type:" prefix, because a control inside a labelled row is already named by
  // the row; everything the pill still carries (the marker, the green value
  // chip, the caret) it carries in the same language, so a reader who has used
  // the Status filter has already learned this one.
  size?: 'md' | 'sm';
}

export function FilterControl({ label, icon, options, selected, onChange, mode = 'multi', clearable = true, disabled = false, disabledHint, allKey, size = 'md' }: FilterControlProps) {
  const [open, setOpen] = useState(false);
  const ref = useClickOutside<HTMLDivElement>(() => setOpen(false));
  const chosen = options.filter((o) => selected.includes(o.key));
  const active = chosen.length > 0 && !(allKey !== undefined && selected.length === 1 && selected[0] === allKey);
  const first = chosen[0];

  const toggle = (key: string) => {
    if (mode === 'single') {
      // Required selector — re-picking the active option keeps it (never empties).
      if (!clearable && selected.includes(key)) {
        setOpen(false);
        return;
      }
      onChange(selected.includes(key) ? [] : [key]);
      setOpen(false);
      return;
    }
    onChange(selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key]);
  };

  // The same string the pill shows, for the accessible name.
  const valueText = active ? pillValue(chosen) : 'All';

  return (
    <div className={'filterctl' + (size === 'sm' ? ' fc-sm' : '')} ref={ref}>
      <button
        type="button"
        className={'fc-pill' + (active ? ' on' : '') + (disabled ? ' is-disabled' : '')}
        // The visible "Type:" prefix is hidden below 361 to buy the value its
        // room, which took the control's NAME with it — a screen reader was
        // left announcing only a mutable selection ("By area", "Status bars")
        // with nothing to say what it changes. Naming it here fixes that, but
        // an aria-label REPLACES the descendant text wholesale, so the value has
        // to come with it or the trade is simply reversed: the control gains a
        // name and loses its state.
        aria-label={label + ': ' + valueText}
        aria-expanded={disabled ? undefined : open}
        aria-haspopup={disabled ? undefined : 'menu'}
        aria-disabled={disabled || undefined}
        disabled={disabled}
        title={disabled ? disabledHint : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        {icon && <Icon name={icon} size={13} className="fc-ico" />}
        <span className="fc-type">{label}:</span>
        {/* The selection reads in the brand green (expert-picker style), led by the
            chosen option's own marker (icon/avatar); "All" when nothing is chosen. */}
        <span className="fc-val">
          {active ? (
            <>
              {optionMark(first!, size === 'sm' ? 14 : 16)}
              {pillValue(chosen)}
            </>
          ) : (
            'All'
          )}
        </span>
        <Icon name="chevron-down" size={13} className="fc-caret" />
      </button>
      {open && !disabled && (
        <FilterMenu
          options={options}
          selected={selected}
          mode={mode}
          clearable={clearable}
          onToggle={toggle}
          onClear={() => {
            onChange([]);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}

// THE PILL'S VALUE (design registry, the multi-select rule): name up to two
// choices, count the rest. "Confirmed, Arrived" says what is chosen; "Confirmed
// +1" made the reader open the menu to learn what the +1 was.
export function pillValue(chosen: FilterOption[]): string {
  if (chosen.length <= 2) return chosen.map((o) => o.label).join(', ');
  return `${chosen[0]!.label}, ${chosen[1]!.label} +${chosen.length - 2}`;
}

export interface FilterMenuProps {
  options: FilterOption[];
  selected: string[];
  mode: 'single' | 'multi';
  clearable: boolean;
  onToggle: (key: string) => void;
  onClear: () => void;
}

// THE OPEN MENU. In single mode a filled row means the one current choice — the
// app's standard selected treatment. In multi mode a TICK means any number:
// rows keep a neutral ground, the tick and the ink carry the selection, and
// hover is the only row fill — because two filled rows stacked read as a
// sandwich, and the fill is a radio affordance that does not survive a second
// selection (Sir, 2026-09-02; viewings-in-the-bookings-list registry). The two
// never share a menu. The empty multi menu shows its empty boxes, so the reader
// knows before touching it that more than one may be chosen.
export function FilterMenu({ options, selected, mode, clearable, onToggle, onClear }: FilterMenuProps) {
  const multi = mode === 'multi';
  const active = selected.length > 0;
  return (
    <div className={'popover fc-menu' + (multi ? ' multi' : '')} role="menu">
      {options.map((o) => {
        const on = selected.includes(o.key);
        return (
          <button
            key={o.key}
            type="button"
            role={multi ? 'menuitemcheckbox' : 'menuitemradio'}
            aria-checked={on}
            className={'fc-opt' + (on ? ' on' : '')}
            onClick={() => onToggle(o.key)}
          >
            {multi && (
              <span className="fc-tick" aria-hidden="true">
                <Icon name="check" size={10} />
              </span>
            )}
            {optionMark(o, o.avatar ? 20 : 15)}
            <span>{o.label}</span>
          </button>
        );
      })}
      {active && clearable && (
        <button type="button" className="fc-clear" onClick={onClear}>
          Clear
        </button>
      )}
    </div>
  );
}
